import { ADMIN_USER_ROLES, normalizeAdminUserRole } from './app';

/**
 * Fiches descriptives des rôles back-office (lecture seule, à but informatif).
 * Les droits réels sont appliqués par le backend (RoleChecker, permissions/roles.py)
 * et le masquage IBAN / BIC par services/pii_masking.py : ces fiches n'accordent aucun droit.
 */

export const IBAN_VISIBILITY = {
    FULL: 'full',
    MASKED: 'masked',
    UNDEFINED: 'undefined',
};

export const IBAN_VISIBILITY_CONFIG = {
    [IBAN_VISIBILITY.FULL]: { label: 'IBAN complet autorisé', variant: 'success' },
    [IBAN_VISIBILITY.MASKED]: { label: 'IBAN toujours masqué', variant: 'warning' },
    [IBAN_VISIBILITY.UNDEFINED]: { label: 'Règle non définie', variant: 'secondary' },
};

const MASKED_IBAN_RULE = 'Format masqué : seuls les 2 premiers et les 2 derniers caractères sont visibles (ex. FR**********************76).';

export const ADMIN_ROLE_DETAILS = {
    Admin: {
        accessLevel: 'Accès complet',
        accessVariant: 'primary',
        audience: "Équipe d'administration",
        permissions: [
            'Accès à tous les endpoints.',
            "Accès à toutes les fonctionnalités d'administration.",
        ],
        objective: null,
        restrictions: ['Aucune restriction spécifique au rôle.'],
        sensitiveData: {
            visibility: IBAN_VISIBILITY.FULL,
            rules: [
                "L'IBAN complet est renvoyé uniquement par les endpoints qui l'autorisent explicitement pour le rôle Admin.",
                "Certains endpoints renvoient un IBAN masqué, y compris pour un administrateur.",
            ],
        },
    },
    Restricted_admin: {
        accessLevel: 'Accès administrateur restreint',
        accessVariant: 'info',
        audience: 'Testeurs travaillant avec des partenaires présélectionnés',
        permissions: [
            'Accès aux endpoints standards.',
            'Accès aux endpoints de référence des sous-couvertures (Reference Subcoverage).',
        ],
        objective: null,
        restrictions: [
            'Accès limité aux endpoints explicitement autorisés.',
            'Données limitées aux partenaires présélectionnés.',
        ],
        sensitiveData: {
            visibility: IBAN_VISIBILITY.MASKED,
            rules: [MASKED_IBAN_RULE, 'Le BIC est également masqué.'],
        },
    },
    Operations_limited: {
        accessLevel: 'Accès opérationnel limité',
        accessVariant: 'warning',
        audience: 'Équipe Opérations',
        permissions: [
            'Accès aux endpoints GET.',
            'Import de nouvelles polices.',
            'Modification des polices existantes.',
        ],
        objective: null,
        restrictions: ['Les autres opérations d’écriture sont interdites sauf autorisation explicite.'],
        sensitiveData: {
            visibility: IBAN_VISIBILITY.MASKED,
            rules: [MASKED_IBAN_RULE, 'Le BIC est également masqué.'],
        },
    },
    Readonly_reporting: {
        accessLevel: 'Lecture seule',
        accessVariant: 'secondary',
        audience: 'Équipe Reporting',
        permissions: ['Accès aux endpoints GET.'],
        objective: 'Consulter les données nécessaires au reporting.',
        restrictions: ['Aucune création, modification ou suppression de données.'],
        sensitiveData: {
            visibility: IBAN_VISIBILITY.MASKED,
            rules: [MASKED_IBAN_RULE, 'Le BIC est également masqué.'],
        },
    },
    Invite_sender: {
        accessLevel: "Accès dédié à l'automatisation des invitations",
        accessVariant: 'dark',
        audience: 'Workflows automatisés Microsoft / n8n',
        permissions: [
            'Accès aux endpoints GET nécessaires au workflow, notamment GET Policies et GET Claims.',
        ],
        objective: "Récupérer les informations nécessaires à l'envoi automatique des invitations.",
        restrictions: ['Accès limité aux endpoints requis par le processus automatisé.'],
        sensitiveData: {
            visibility: IBAN_VISIBILITY.UNDEFINED,
            rules: [
                "Aucune règle de visibilité IBAN n'est définie explicitement pour ce rôle dans le code.",
                'Par défaut, un rôle non administrateur ne reçoit pas d’IBAN complet ; à confirmer avec l’équipe backend.',
            ],
        },
    },
};

export const SENSITIVE_DATA_GENERAL_RULES = [
    'Le masquage des IBAN est appliqué par le backend, jamais uniquement par l’interface.',
    'Un IBAN complet n’est jamais exposé à un utilisateur non autorisé.',
    'Les droits IBAN ne se déduisent pas du seul nom du rôle : ils dépendent des règles de chaque endpoint.',
];

/** Liste ordonnée (même ordre que ADMIN_USER_ROLES) des rôles avec leur fiche. */
export const ADMIN_ROLES_WITH_DETAILS = ADMIN_USER_ROLES.map((role) => ({
    ...role,
    ...ADMIN_ROLE_DETAILS[role.value],
}));

/** Retourne la fiche d'un rôle (insensible à la casse) ou null si le rôle est inconnu. */
export const getAdminRoleDetails = (role) => {
    const value = normalizeAdminUserRole(role);
    if (!value) return null;
    return ADMIN_ROLES_WITH_DETAILS.find((item) => item.value === value) || null;
};
